import { HttpException } from '../../../utils/exceptions/httpException';
import Job from '../models/job.model';
import { TFunction } from 'i18next';
import { status } from '../../../utils/helpers/api.responses';
import Container, { Service } from 'typedi';
import mongoose, { ClientSession } from 'mongoose';
import { IJob } from '../interfaces/job.interface';
import AllocationRequest from '../../../Modules/allocationRequest/models/allocationRequest.model';
import { AllocationRequestStatusEnum } from '../../../Modules/allocationRequest/interfaces/allocationRequest.interface';
import Labour from '../../../Modules/Labour/models/labour.model';
import { AvailableStatusEnum } from '../../../Modules/Labour/interfaces/labour.interface';
import Availability from '../../../Modules/availabilityManagement/models/availability.model';
import { NotificationService } from '../../../Modules/Notification/services/notification.service';
import User from '../../../Modules/Auth/models/auth.model';
import { RolesEnum } from '../../../Modules/Auth/interfaces/auth.interface';

@Service()
export default class JobService {
    public notificationService = Container.get(NotificationService)

    public async createJob(jobData: Partial<IJob>, t: TFunction): Promise<IJob> {
        try {
            const { companyName, location, noOfSpots, note, createdBy } = jobData;
            if (!companyName || !location || !noOfSpots || !createdBy) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'companyName, location, noOfSpots, createdBy' }));
            }
            if (noOfSpots < 1) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'noOfSpots' }));
            }

            const existingJob = await Job.findOne({ companyName, location, isDeleted: false });
            if (existingJob) {
                throw new HttpException(status.ResourceExist, t('General.already_exists', { field: t('Job.job') }));
            }
            const lastJob = await Job.findOne({}).sort({ srno: -1 }).select('srno').lean();
            let newSrno = '001';
            if (lastJob?.srno) {
                const lastSrnoNumber = parseInt(lastJob.srno, 10);
                newSrno = (lastSrnoNumber + 1).toString().padStart(3, '0');
            }
            const job = new Job({
                companyName,
                location,
                noOfSpots,
                note,
                createdBy: new mongoose.Types.ObjectId(createdBy),
                srno: newSrno
            });
            return await job.save();
        } catch (error) {
            throw error instanceof HttpException ? error : new HttpException(status.InternalServerError, t('General.errorCreating', { field: t('Job.job') }));
        }
    }

    public async getJobs({
        filters,
        page = 1,
        limit = 10,
        search = "",
        sortBy = "createdAt",
        sortOrder = "desc",
        t,
    }: {
        filters: { Jobstatus?: string;[key: string]: any };
        page: number;
        limit: number;
        search: string;
        sortBy: string;
        sortOrder: string;
        t: TFunction;
    }): Promise<{ data: IJob[]; total: number; page: number; totalPages: number }> {
        try {
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined) {
                    delete filters[key];
                }
            });
            const query: any = { isDeleted: false, ...filters };

            if (search) {
                query.companyName = { $regex: search, $options: "i" };
            }


            let sortOption: any = {};
            if (sortBy) {
                sortOption[sortBy] = sortOrder === "desc" ? -1 : 1;
            }

            const skip = (page - 1) * limit;

            const [jobs, total] = await Promise.all([
                Job.find(query)
                    .sort(sortOption)
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                Job.countDocuments(query),
            ]);

            return {
                data: jobs,
                total,
                page,
                totalPages: Math.ceil(total / limit),
            };
        } catch (error) {
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Job.jobs') }));
        }
    }

    public async getOpenJobs({
        filters,
        page = 1,
        limit = 10,
        search = "",
        sortBy = "createdAt",
        sortOrder = "desc",
        t,
    }: {
        filters: { Jobstatus?: string;[key: string]: any };
        page: number;
        limit: number;
        search: string;
        sortBy: string;
        sortOrder: string;
        t: TFunction;
    }): Promise<{ data: IJob[]; total: number; page: number; totalPages: number }> {
        try {
            Object.keys(filters).forEach(key => {
                if (filters[key] === undefined) {
                    delete filters[key];
                }
            });
            const query: any = {
                isDeleted: false,
                open: { $gt: 0 },
                ...filters,
            };
            if (search) {
                query.companyName = { $regex: search, $options: "i" };
            }


            let sortOption: any = {};
            if (sortBy) {
                sortOption[sortBy] = sortOrder === "desc" ? -1 : 1;
            }

            const skip = (page - 1) * limit;

            const [jobs, total] = await Promise.all([
                Job.find(query)
                    .sort(sortOption)
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                Job.countDocuments(query),
            ]);

            return {
                data: jobs,
                total,
                page,
                totalPages: Math.ceil(total / limit),
            };
        } catch (error) {
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Job.jobs') }));
        }
    }

    public async getJobById(id: string, t: TFunction, session?: ClientSession): Promise<IJob> {
        try {
            const job = await Job.findById(id).session(session || null);

            if (!job || job.isDeleted) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Job.job') }));
            }
            return job;
        } catch (error) {
            throw error instanceof HttpException ? error : new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Job.job') }));
        }
    }

    public async updateJob(id: string, jobData: Partial<IJob>, t: TFunction): Promise<IJob> {
        try {
            const job = await this.getJobById(id, t);
            if (job.status === 'Closed') {
                throw new HttpException(status.BadRequest, t('General.cannot_update_closed', { field: t('Job.job') }));
            }
            const { companyName, location, noOfSpots, note } = jobData;
            Object.assign(job, {
                companyName: companyName || job.companyName,
                location: location || job.location,
                noOfSpots: noOfSpots !== undefined ? noOfSpots : job.noOfSpots,
                note: note || job.note,
                open: noOfSpots !== undefined ? (noOfSpots - job.allotted) : job.open
            });
            if (noOfSpots && noOfSpots < job.allotted) {
                throw new HttpException(status.BadRequest, t('General.invalid_spots', { field: t('Job.job') }));
            }
            return await job.save();
        } catch (error) {
            throw error instanceof HttpException ? error : new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Job.job') }));
        }
    }

    public async deleteJob(ids: string[], t: TFunction): Promise<void> {
        const session = await mongoose.startSession();
        let transactionCommitted = false

        try {
            session.startTransaction();
            const jobs = await Job.find({ _id: { $in: ids }, isDeleted: false })
                .session(session)
                .lean();

            if (jobs.length !== ids.length) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Job.job') }));
            }

            for (const job of jobs) {
                const allocationRequests = await AllocationRequest.find({ jobId: job._id, isDeleted: false })
                    .select('status')
                    .session(session)
                    .lean();

                const acceptedAllocations = allocationRequests.filter(ar => ar.status.toLowerCase() === AllocationRequestStatusEnum.ACCEPTED.toLowerCase());
                if (job.status === 'Open' && acceptedAllocations.length > 0) {
                    throw new HttpException(status.BadRequest, t('General.cannot_delete_open_with_allocations', { field: t('Job.job') }));
                }
                if (job.status === 'Open' && job.allotted > 0) {
                    throw new HttpException(status.BadRequest, t('General.close_job_first', { field: t('Job.job') }));
                }

                await Job.updateOne(
                    { _id: job._id },
                    { $set: { isDeleted: true, deletedAt: new Date() } },
                    { session }
                );
            }

            await session.commitTransaction();
            transactionCommitted = true

        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            } throw error instanceof HttpException ? error : new HttpException(status.InternalServerError, t('General.errorDeleting', { field: t('Job.job') }));
        } finally {
            await session.endSession();
        }
    }
    public async closeJob(id: string, t: TFunction, session?: ClientSession): Promise<IJob> {
        try {
            const job = await Job.findById(id).session(session);
            if (!job || job.isDeleted) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Job.job') }));
            }
            if (job.status === 'Closed') {
                throw new HttpException(status.BadRequest, t('General.already_closed', { field: t('Job.job') }));
            }
            // if (job.noOfSpots !== job.allotted) {
            //     throw new HttpException(status.BadRequest, t('General.spots_not_filled', { field: t('Job.job') }));
            // }
            job.status = 'Closed';
            const savedJob = await job.save({ session });
            const pendingRequests = await AllocationRequest.find({
                jobId: id,
                status: { $regex: `^${AllocationRequestStatusEnum.PENDING}$`, $options: 'i' },
                isDeleted: false
            });
            // const userIds = pendingRequests.map(req => req.userId);

            // const users = await User.find({ _id: { $in: userIds } })
            //     .select('firstName lastName')
            //     .lean();

            // const userMap = new Map(users.map(user => [user._id.toString(), user]));

            // await Promise.all(
            //     pendingRequests.map(async (request) => {
            //         const user = userMap.get(request.userId.toString());
            //         if (!user) return;

            //         await this.notificationService.sendNotification(
            //             'jobManagement',
            //             'jobClosedPendingRequest',
            //             {
            //                 to: request.userId,
            //                 entityData: {
            //                     jobId: id,
            //                     allocationRequestId: request._id,
            //                     companyName: job.companyName,
            //                     location: job.location,
            //                     startDate: request.startDate,
            //                     endDate: request.endDate,
            //                     firstName: user.firstName,
            //                     lastName: user.lastName
            //                 }
            //             },
            //             t
            //         );
            //     })
            // );


            return savedJob
        } catch (error) {
            throw error instanceof HttpException ? error : new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Job.job') }));
        }
    }


    public async hardDeleteJob(ids: string[], t: TFunction, updatedBy: string): Promise<void> {
        const session = await mongoose.startSession();
        let transactionCommitted = false;

        try {
            session.startTransaction();

            const jobs = await Job.find({ _id: { $in: ids }, isDeleted: false }).session(session).lean();
            if (jobs.length !== ids.length) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('Job.job') }));
            }

            const jobIds = jobs.map(job => job._id);

            const allocationRequests = await AllocationRequest.find({
                jobId: { $in: jobIds },
                isDeleted: false,
            }).select('jobId userId status').session(session).lean();

            const acceptedAllocations = allocationRequests.filter(ar =>
                ar.status.toLowerCase() === AllocationRequestStatusEnum.ACCEPTED.toLowerCase()
            );

            const affectedUserIds = [...new Set(acceptedAllocations.map(ar => ar.userId.toString()))];

            const [labours, otherAllocationsMap] = await Promise.all([
                Labour.find({ userId: { $in: affectedUserIds }, isDeleted: false }).session(session).lean(),
                AllocationRequest.aggregate([
                    {
                        $match: {
                            userId: { $in: affectedUserIds.map(id => new mongoose.Types.ObjectId(id)) },
                            jobId: { $nin: jobIds },
                            status: AllocationRequestStatusEnum.ACCEPTED,
                            isDeleted: false,
                        }
                    },
                    {
                        $group: {
                            _id: "$userId",
                            count: { $sum: 1 }
                        }
                    }
                ]).session(session)
            ]);

            const labourMap = new Map(labours.map(l => [l.userId.toString(), l]));
            const otherAllocationsMapObj = new Map(otherAllocationsMap.map(a => [a._id.toString(), a.count]));

            const updateLabourAndAvailabilityPromises = acceptedAllocations.map(ar => {
                const userIdStr = ar.userId.toString();
                const labour = labourMap.get(userIdStr);
                if (!labour) return null;

                const availabilityStatus = otherAllocationsMapObj.get(userIdStr) > 0
                    ? AvailableStatusEnum.UNAVAILABLE
                    : AvailableStatusEnum.AVAILABLE;

                return Promise.all([
                    Labour.updateOne(
                        { userId: ar.userId },
                        { $set: { availability_status: availabilityStatus } },
                        { session }
                    ).lean(),
                    Availability.deleteOne({
                        userId: ar.userId,
                        startDate: { $lte: allocationRequests.find(j => j._id.toString() === ar.jobId.toString())?.endDate },
                        endDate: { $gte: allocationRequests.find(j => j._id.toString() === ar.jobId.toString())?.startDate },
                        isDeleted: false,
                    }).session(session)
                ]);
            }).filter(Boolean);

            const allocationDeletions = AllocationRequest.deleteMany({ jobId: { $in: jobIds } }, { session });
            const jobDeletions = Job.deleteMany({ _id: { $in: jobIds } }, { session });

            await Promise.all([
                ...updateLabourAndAvailabilityPromises.flat(),
                allocationDeletions,
                jobDeletions
            ]);

            await session.commitTransaction();
            transactionCommitted = true;

            const [deleter, acceptedUserRequests, adminUsers] = await Promise.all([
                User.findById(updatedBy)
                    .select('firstName lastName email roleId')
                    .populate<{ roleId: { name: string } }>('roleId', 'name')
                    .lean(),
                AllocationRequest.find({
                    jobId: { $in: jobIds },
                    status: { $regex: `^${AllocationRequestStatusEnum.ACCEPTED}$`, $options: 'i' },
                    isDeleted: false,
                }).select('jobId userId').lean(),
                User.find({ isDeleted: false })
                    .populate({
                        path: 'roleId',
                        match: { name: { $regex: `^${RolesEnum.ADMIN}$`, $options: 'i' } },
                        select: 'name',
                    })
                    .select('_id')
                    .lean(),
            ]);

            const adminIds = adminUsers.filter(u => u.roleId).map(u => new mongoose.Types.ObjectId(u._id));

            const notificationPromises: Promise<any>[] = [];

            for (const job of jobs) {
                const jobAcceptedUsers = acceptedUserRequests
                    .filter(ar => ar.jobId.toString() === job._id.toString())
                    .map(ar => ar.userId);

                if (jobAcceptedUsers.length > 0) {
                    notificationPromises.push(
                        this.notificationService.sendNotification(
                            'jobManagement',
                            'jobHardDeleted',
                            {
                                from: new mongoose.Types.ObjectId(updatedBy),
                                to: jobAcceptedUsers,
                                entityData: {
                                    jobId: job._id.toString(),
                                    companyName: job.companyName,
                                    location: job.location,
                                },
                            },
                            t
                        )
                    );
                }

                if (deleter?.roleId?.name.toLowerCase() === RolesEnum.STAFF.toLowerCase() && adminIds.length > 0) {
                    notificationPromises.push(
                        this.notificationService.sendNotification(
                            'jobManagement',
                            'jobHardDeletedByStaff',
                            {
                                from: new mongoose.Types.ObjectId(updatedBy),
                                to: adminIds,
                                entityData: {
                                    jobId: job._id.toString(),
                                    companyName: job.companyName,
                                    location: job.location,
                                    staffName: deleter.firstName || deleter.email || 'Unknown Staff',
                                    targetName: 'Job',
                                    firstName: deleter.firstName || '',
                                    lastName: deleter.lastName || '',
                                },
                            },
                            t
                        )
                    );
                }
            }

            await Promise.all(notificationPromises);
        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            throw error instanceof HttpException
                ? error
                : new HttpException(status.InternalServerError, t('General.errorDeleting', { field: t('Job.job') }));
        } finally {
            await session.endSession();
        }
    }

}